import React from "react";
//#region redux
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "store";
import { displayDialog } from "store/actions/utilActions";
import { ThunkDispatch } from "redux-thunk";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { IconDefinitionConstants, InvoiceConstants } from "utils/constants";
import { SelectInputItemProps, ToggleProps } from "models/propTypes/wintFormPropsTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { DimensionEnums, InvoiceEnums, StyleEnums } from "models/enums";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { ContentSelectorProps, InvoiceContentSelectionFormType } from "models/propTypes/invoicePropTypes";
import { InvoiceFormTypes } from "models/formTypes";
import { PeriodisationMode } from "models/utilTypes/periodisationTypes";
import { WintFormErrors, WintFormKey, WintFormTouched } from "models/propTypes/wintFormPropsTypes";
//#endregion
//#region components
import {
  CurrencyDisplay,
  HorizontalFormField,
  WizardSection,
  Toggle,
  Suggestions,
  DimensionChipSelector,
  EditableTableTabHeader,
  useWizardStepContext,
  CurrencySelectionInput,
} from "components/common/wintForms";
import DraggableInvoiceProductRow from "./draggableProductRow";
import { TableContainer, Table, TableBody, TableRow, TableCell, Grid, Tabs, Tab } from "@mui/material";
import { useArticleSearch, useCreateArticleDialog } from "components/article";
import { MessageBubble, SimpleMessage, TableFooter } from "components/common";
import { useLocation, useNavigate } from "react-router-dom";
import { useInvoiceService, useArticleService, useRecurringInvoiceService } from "serviceHooks";
//#endregion
import { DateHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const ContentSelectionStep: React.FC<ContentSelectorProps<InvoiceContentSelectionFormType>> = (props) => {
  const articleService = useArticleService();
  const invoiceService = useInvoiceService();
  const recurringInvoiceService = useRecurringInvoiceService();

  const {
    connect,
    values,
    errors,
    touched,
    submitted,
    wizardProps: wizardStepProps,
    isRecurring,
    isQuotation,
    setValues,
  } = props;
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  const wizardStepContext = useWizardStepContext();
  const allowedTypes = invoiceService.getCompatibleDimensionTypes(isQuotation);

  const [topArticles, setTopArticles] = React.useState<AutocompleteListItem[]>([]);
  const [units, setUnits] = React.useState<SelectInputItemProps[]>([]);

  const onArticleSearch = useArticleSearch();
  const noVatCompany = useSelector((store: AppState) => store.auth.selectedCompany.noVat);
  const vatLockedToVatFree = noVatCompany;

  const dimensions = useSelector(
    (store: AppState) =>
      store.common.dimensions.invoiceDimensions?.filter((d) => allowedTypes.includes(d.key.type)) ?? []
  );
  const recommendedDimensions = dimensions.map((d) => d.key.recommendedDimensionId).filter((d) => d) as string[];

  const defaultProductRow = React.useMemo(
    (): InvoiceFormTypes.Product => ({
      uniqueId: UtilsHelper.getUniqueId(),
      id: 0,
      quantity: 1,
      price: 0,
      tax: vatLockedToVatFree ? 0 : InvoiceConstants.DefaultVatType,
      taxText: vatLockedToVatFree
        ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.VATFree)
        : `${InvoiceConstants.DefaultVatType}%`,
      unit: units.length > 0 ? (units.find((u) => u.label === InvoiceConstants.DefaultUnit)?.value as number) : 0,
      eUVatCategoryId: WintSuperkollAPIModel.EuVatProductType.NotDefined,
      periodisation: {
        mode: values.isPeriodisationByDate ? "date" : "month",
      },
      recurringPeriodisation: {
        from: 0,
        duration: recurringInvoiceService.getFrequencyMonths(
          ("recurringSettings" in values ? values.recurringSettings.recurringInvoiceFrequency : undefined) ??
            WintSuperkollAPIModel.RecurringInvoiceFrequency.Monthly
        ),
        isEnabled: true,
      },
      dimensions: recommendedDimensions,
    }),
    [
      units,
      vatLockedToVatFree,
      recommendedDimensions,
      values.isPeriodisationByDate,
      (values as any).recurringSettings?.recurringInvoiceFrequency,
    ]
  );

  React.useEffect(() => {
    invoiceService.getUnits().then((res) => {
      const newUnits = res.map((u) => ({ value: u.id, label: u.name ?? "" }));
      setUnits([{ value: 0, label: UtilsHelper.getSelectionNoneText() }, ...newUnits.reverse()]);
    });
    invoiceService.getArticleSuggestions().then((res) => {
      if (res && res.length > 0) {
        setTopArticles(
          res.map(
            (item): AutocompleteListItem => ({
              id: item.id ?? 0,
              value: item.name ?? "",
              icon: IconDefinitionConstants.WBuilding,
              data: item,
            })
          )
        );
      } else if (topArticles.length !== 0) setTopArticles([]);
    });
  }, []);

  const rows = [...(values.products ?? []), defaultProductRow];

  const totalAmount =
    (values.products ?? [])
      .map((row) => invoiceService.calculateAmount(row.price ?? 0, row.quantity ?? 0))
      .reduce((x, y) => x + y, 0) ?? 0;

  const totalVAT =
    (values.products ?? [])
      .map((row) => invoiceService.calculateTax(row.price ?? 0, row.tax ?? 0, row.quantity ?? 0))
      .reduce((x, y) => x + y, 0) ?? 0;

  const [currentTab, setCurrentTab] = React.useState(InvoiceEnums.InvoiceProductTabs.Amount);

  const removeRow = React.useCallback(
    (index: number) => {
      const rows = [...(values.products ?? [])];
      const removedRow = rows.splice(index, 1)[0];
      let newMasterRow = { ...rows[0] };
      if (
        rows.length > 0 &&
        index === 0 &&
        (!values.isDimensionsAdvancedConfiguration || !values.isPeriodisationAdvancedConfiguration)
      ) {
        if (!values.isDimensionsAdvancedConfiguration) {
          newMasterRow = { ...newMasterRow, dimensions: removedRow.dimensions };
        }
        if (!values.isPeriodisationAdvancedConfiguration) {
          newMasterRow = { ...newMasterRow, periodisation: removedRow.periodisation };
        }
        rows[0] = newMasterRow;
      }
      connect("products").setValue(rows, true);
    },
    [values]
  );

  const maxRowsPerPage = 30;
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPage] = React.useState<number>(maxRowsPerPage);
  const pageStartIndex = page * rowsPerPage;
  const pageEndIndex = pageStartIndex + rowsPerPage;

  const onPageChange = React.useCallback((pageNumber: number) => {
    setPage(pageNumber);
  }, []);

  const changeRowsPerPageHandler = React.useCallback((rowsPerPage: number) => {
    setPage(0);
    setRowsPage(rowsPerPage);
  }, []);

  const masterRow = values.products ? values.products[0] : undefined;

  const masterRowDimensions = React.useMemo(() => {
    if (!masterRow) return "";

    return dimensions
      .flatMap((d) => d.value)
      .filter((d) => masterRow.dimensions?.includes(d.id ?? ""))
      .map((d) => d.name)
      .join(", ");
  }, [dimensions, masterRow]);

  const masterRowPeriodisation = React.useMemo(() => {
    if (!masterRow) return "";
    const range = masterRow.periodisation?.range;
    if (!range) return "";
    return masterRow.periodisation?.mode === "date"
      ? DateHelper.formatDateRange(range.start ?? undefined, range.end ?? undefined)
      : DateHelper.formatMonthRange(range.start ?? undefined, range.end ?? undefined);
  }, [masterRow]);

  const masterRowRecurringPeriodisation = React.useMemo(() => {
    if (!masterRow) return "";
    const recurringPeriodisation = masterRow.recurringPeriodisation;
    if (!recurringPeriodisation) return "";
    return `${LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.Invoice.RecurringPeriodisationDuration
    )}: ${
      recurringPeriodisation?.duration ??
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NotSelected)
    } ${LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.Common.MonthOrMonths
    )}, ${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.RecurringPeriodisationStart)}: ${
      recurringPeriodisation?.from ?? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NotSelected)
    } ${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.MonthOrMonths)}`;
  }, [masterRow]);

  const updateRow = React.useCallback((index: number, newRow: any, shouldValidate?: boolean) => {
    connect(`products[${index}]` as WintFormKey<InvoiceContentSelectionFormType>).setValue(newRow, shouldValidate);
  }, []);

  const onArticleSelect = (index: number, value?: AutocompleteListItem) => {
    const oldRow = index === (values.products ?? []).length ? defaultProductRow : values.products![index];
    const newRow: InvoiceFormTypes.Product = { ...oldRow, article: value };
    if (value) {
      newRow.text = value.value;
      if (value.data) {
        const article: WintSuperkollAPIModel.ArticleSearchResponse = value.data;
        newRow.price = article.standardPrice;
        newRow.unit = article.unit?.id ?? 0;
        newRow.tax = vatLockedToVatFree ? 0 : article.vat;
        newRow.eUVatCategoryId = article.eUVatCategory;
        newRow.articleAccount = article.account;
        newRow.articleVat = article.vat;
        newRow.articleIsAgentSale = article.isAgentSaleArticle;
      } else {
        // Free-text/custom entry with no matching article data - no article to lock the VAT to.
        newRow.articleAccount = undefined;
        newRow.articleVat = undefined;
        newRow.articleIsAgentSale = undefined;
      }
    } else {
      // Article deselected entirely - clear the lock flag ("Avmarkering av artikel rensar låsflaggan").
      newRow.articleAccount = undefined;
      newRow.articleVat = undefined;
      newRow.articleIsAgentSale = undefined;
    }
    updateRow(index, newRow, index === 0);
  };

  const onUnitSelect = (index: number, value?: number) => {
    const oldRow = index === (values.products ?? []).length ? defaultProductRow : values.products![index];
    const newRow: InvoiceFormTypes.Product = { ...oldRow, unit: value, quantity: !value ? 1 : oldRow.quantity };

    updateRow(index, newRow, true);
  };

  const getCreateDialog = useCreateArticleDialog();
  const onAddArticle = () => {
    dispatch(displayDialog(getCreateDialog()));
  };

  const productsConnector = connect("products");
  const currencyError = errors["currency"];
  const productsTouched = touched["products"] ?? false;
  const hasErrors = productsConnector.hasErrors || currencyError !== undefined;

  const contentSummary = `${values.products
    ?.slice(0, 2)
    .map((p) => p?.article?.value ?? "")
    .map((n, i, a) => {
      const maxLength = a.length === 1 || a.join().length < 50 ? 50 : 25;
      return n.substring(0, maxLength) + (n.length > maxLength ? "..." : "");
    })
    .join(", ")}${
    values.products && values.products.length > 2
      ? ` +${values.products.length - 2} ${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.More)}`
      : ""
  }`;

  const dimensionTabHasErrors =
    Array.isArray(errors.products) && errors.products?.some((p) => !UtilsHelper.isEmpty(p?.dimensions));
  const otherTabHasErrors =
    Array.isArray(errors.products) && errors.products?.some((p) => !UtilsHelper.isEmpty(p?.eUVatCategoryId));
  const periodisationTabHasErrors =
    Array.isArray(errors.products) &&
    errors.products?.some((p) =>
      isRecurring ? !UtilsHelper.isEmpty(p?.recurringPeriodisation) : !UtilsHelper.isEmpty(p?.periodisation)
    );

  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    const state: any = location.state;

    if (state && typeof state === "object") {
      if (state.focusField) {
        const setFocusField = (tab: InvoiceEnums.InvoiceProductTabs) => {
          setCurrentTab(tab);
          wizardStepProps.goToStep(wizardStepContext.index);
          delete state.focusField;
          navigate(location, { replace: true });
        };

        const dimensionFieldName: WintFormKey<InvoiceFormTypes.Create> = "products.dimensions";
        const periodisationFieldName: WintFormKey<InvoiceFormTypes.Create> = "products.periodisation";
        switch (state.focusField) {
          case dimensionFieldName:
            setFocusField(InvoiceEnums.InvoiceProductTabs.Dimensions);
            break;
          case periodisationFieldName:
            setFocusField(InvoiceEnums.InvoiceProductTabs.Periodisation);
            break;
        }
      }

      if (state.productId) {
        const productId = state.productId;
        if (!props.invoiceId) {
          articleService.get(productId).then((article) => {
            const newProduct: InvoiceFormTypes.Product = {
              ...defaultProductRow,
              uniqueId: UtilsHelper.getUniqueId(),
              id: article.id,
              article: { id: article.id, value: article.text ?? "" },
              quantity: 1,
              price: article.unitPrice,
              tax: vatLockedToVatFree ? 0 : article.vat,
              unit: article.unitId,
              text: article.text,
              eUVatCategoryId: article.euVatProductType,
            };

            updateRow(0, newProduct, true);
          });
        }

        delete state.productId;
        navigate(location, { replace: true });
      }
    }
  }, [location.state]);

  const moveRow = React.useCallback(
    (source: number, target: number) => {
      const newProducts = [...values.products!];

      if (!(values.isDimensionsAdvancedConfiguration ?? false) && (source === 0 || target === 0)) {
        const masterRow = newProducts[0];
        newProducts[0] = {
          ...masterRow,
          dimensions: undefined,
        };

        const newMasterRowIndex = Math.max(1, source);
        const newMasterRow = newProducts[newMasterRowIndex];

        newProducts[newMasterRowIndex] = {
          ...newMasterRow,
          dimensions: masterRow.dimensions ? [...masterRow.dimensions] : undefined,
        };
      }

      if (!(values.isPeriodisationAdvancedConfiguration ?? false) && (source === 0 || target === 0)) {
        const masterRow = newProducts[0];
        newProducts[0] = {
          ...masterRow,
          periodisation: undefined,
        };

        const newMasterRowIndex = Math.max(1, source);
        const newMasterRow = newProducts[newMasterRowIndex];

        newProducts[newMasterRowIndex] = {
          ...newMasterRow,
          periodisation: masterRow.periodisation ? { ...masterRow.periodisation } : undefined,
        };
      }

      const [sourceItem] = newProducts.splice(source, 1);
      newProducts.splice(target, 0, sourceItem);

      connect("products").setValue(newProducts, false);
    },
    [values.products, values.isDimensionsAdvancedConfiguration, values.isPeriodisationAdvancedConfiguration]
  );

  React.useEffect(() => {
    if (values.isDimensionsAdvancedConfiguration && touched.isDimensionsAdvancedConfiguration) {
      setValues((prevValues) => {
        const formattedValues: InvoiceContentSelectionFormType =
          invoiceService.formatInvoiceFormMasterRowConfigurations({
            ...prevValues,
            isDimensionsAdvancedConfiguration: false,
          });
        return {
          ...formattedValues,
          isDimensionsAdvancedConfiguration: true,
        };
      });
    }
  }, [values.isDimensionsAdvancedConfiguration]);

  React.useEffect(() => {
    if (values.isPeriodisationAdvancedConfiguration && touched.isPeriodisationAdvancedConfiguration) {
      setValues((prevValues) => ({
        ...invoiceService.formatInvoiceFormMasterRowConfigurations({
          ...prevValues,
          isPeriodisationAdvancedConfiguration: false,
        }),
        isPeriodisationAdvancedConfiguration: true,
        isPeriodisationByDate: prevValues.products?.[0]?.periodisation?.mode === "date",
      }));
    }
  }, [values.isPeriodisationAdvancedConfiguration]);

  React.useEffect(() => {
    if (!values.isPeriodisationAdvancedConfiguration) return;
    const targetMode: PeriodisationMode = values.isPeriodisationByDate ? "date" : "month";
    const products = values.products ?? [];
    const allMatch = products.every((p) => (p.periodisation?.mode ?? "month") === targetMode);
    if (allMatch) return;
    setValues((prevValues) => ({
      ...prevValues,
      products: prevValues.products?.map((p) => {
        const currentMode = p.periodisation?.mode ?? "month";
        if (currentMode === targetMode) return p;
        return {
          ...p,
          periodisation: {
            ...p.periodisation,
            mode: targetMode,
            range: undefined,
          },
        };
      }),
    }));
  }, [values.isPeriodisationByDate]);

  const productsErrorsValues = (errors.products as unknown as WintFormErrors<InvoiceFormTypes.Product>[]) ?? [];
  const productsTouchedValues = (touched.products as unknown as WintFormTouched<InvoiceFormTypes.Product>[]) ?? [];

  return (
    <WizardSection
      header={TranslationKeys.Invoice.Content}
      description={
        hasErrors
          ? isQuotation
            ? TranslationKeys.Quotation.ContentDescription
            : TranslationKeys.Invoice.ContentDescription
          : contentSummary
      }
      submitted={submitted}
      hasErrors={hasErrors}
      wizardProps={wizardStepProps}
      testId="contentSelectionStep"
    >
      <Grid xs={12} container item className={clsx(cssExports.tableListContainer, cssExports.editableTableContainer)}>
        <Grid container direction="column" className={cssExports.formFieldMessageAttachElemTop}>
          {typeof productsConnector.error === "string" && (
            <MessageBubble
              message={LocalizationHelperInstance.getFormattedMessage(productsConnector.error)}
              type={productsTouched ? StyleEnums.DisplayType.Danger : StyleEnums.DisplayType.Info}
              placement="top"
            ></MessageBubble>
          )}
          <TableContainer className={cssExports.tableContainerOuter} classes={{ root: cssExports.tableContainerRoot }}>
            <Table>
              <TableBody className={cssExports.tableBody}>
                <TableRow className={cssExports.tabRow}>
                  <TableCell colSpan={2} />
                  <TableCell colSpan={6}>
                    <Tabs
                      value={currentTab}
                      className={cssExports.tabs}
                      classes={{ indicator: cssExports.tabIndicator, flexContainer: cssExports.tabFlexContainer }}
                      onChange={(e: any, value) => setCurrentTab(value)}
                    >
                      <Tab
                        label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Article)}
                        className={cssExports.tab}
                        classes={{ selected: cssExports.tabSelected, root: cssExports.tabWrapper }}
                        value={InvoiceEnums.InvoiceProductTabs.Amount}
                      />
                      <Tab
                        label={LocalizationHelperInstance.getFormattedMessage(
                          isQuotation ? TranslationKeys.Quotation.QuotationText : TranslationKeys.Invoice.InvoiceText
                        )}
                        className={cssExports.tab}
                        classes={{ selected: cssExports.tabSelected, root: cssExports.tabWrapper }}
                        value={InvoiceEnums.InvoiceProductTabs.Text}
                      />
                      {!isQuotation && (
                        <Tab
                          label={
                            <EditableTableTabHeader tabContentHasError={periodisationTabHasErrors}>
                              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Accrual)}
                            </EditableTableTabHeader>
                          }
                          className={cssExports.tab}
                          classes={{ selected: cssExports.tabSelected, root: cssExports.tabWrapper }}
                          value={InvoiceEnums.InvoiceProductTabs.Periodisation}
                        />
                      )}
                      {dimensions?.length && (
                        <Tab
                          label={
                            <EditableTableTabHeader tabContentHasError={dimensionTabHasErrors}>
                              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Tags)}
                            </EditableTableTabHeader>
                          }
                          className={cssExports.tab}
                          classes={{ selected: cssExports.tabSelected, root: cssExports.tabWrapper }}
                          value={InvoiceEnums.InvoiceProductTabs.Dimensions}
                        />
                      )}
                      {!isQuotation && (
                        <Tab
                          label={
                            <EditableTableTabHeader tabContentHasError={otherTabHasErrors}>
                              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Other)}
                            </EditableTableTabHeader>
                          }
                          className={cssExports.tab}
                          classes={{ selected: cssExports.tabSelected, root: cssExports.tabWrapper }}
                          value={InvoiceEnums.InvoiceProductTabs.EUVatCategory}
                        />
                      )}
                    </Tabs>
                  </TableCell>
                </TableRow>
                {rows.slice(pageStartIndex, pageEndIndex).map((row, index) => {
                  const rowIndex = pageStartIndex + index;
                  const isLastRow = rowIndex === rows.length - 1;
                  return (
                    <DraggableInvoiceProductRow
                      key={row.uniqueId}
                      formValues={values}
                      connectToForm={connect}
                      row={row}
                      errors={productsErrorsValues[rowIndex]}
                      touched={productsTouchedValues[rowIndex]}
                      index={rowIndex}
                      currentTab={currentTab}
                      onArticleSearch={onArticleSearch}
                      suggestedArticles={topArticles}
                      updateRow={updateRow.bind(undefined, rowIndex)}
                      onArticleSelect={onArticleSelect.bind(undefined, rowIndex)}
                      onUnitSelect={onUnitSelect.bind(undefined, rowIndex)}
                      isLastRow={isLastRow}
                      removeRow={removeRow}
                      units={units}
                      masterRow={rowIndex === 0}
                      rowLevelDimensions={values.isDimensionsAdvancedConfiguration ?? false}
                      rowLevelPeriodisation={values.isPeriodisationAdvancedConfiguration ?? false}
                      disablePeriodisationModeToggle={values.isPeriodisationAdvancedConfiguration ?? false}
                      masterRowDimensions={masterRowDimensions}
                      masterRowPeriodisation={masterRowPeriodisation}
                      masterRowRecurringPeriodisation={masterRowRecurringPeriodisation}
                      isRecurring={isRecurring}
                      isQuotation={isQuotation}
                      isEUCustomer={values.isEUCustomer}
                      revenueAccountsEnabled={props.revenueAccountsEnabled}
                      vatLockedToVatFree={vatLockedToVatFree}
                      moveRow={moveRow}
                      canDrag={!isLastRow && rows.length > 2}
                    />
                  );
                })}
              </TableBody>
            </Table>
            <Grid container className={cssExports.tablePaginationContainer}>
              <TableFooter
                total={rows.length}
                rowsPerPage={rowsPerPage}
                maxRowsPerPage={maxRowsPerPage}
                currentPage={page}
                advancedPagination
                hideIfSinglePage
                changePageHandler={onPageChange}
                changeRowsPerPageHandler={changeRowsPerPageHandler}
              />
            </Grid>
          </TableContainer>
        </Grid>

        {currentTab === InvoiceEnums.InvoiceProductTabs.Dimensions && !isQuotation ? (
          <Grid container>
            <Grid item className={cssExports.tableMasterRowController}>
              <HorizontalFormField<ToggleProps>
                Component={Toggle}
                {...connect("isDimensionsAdvancedConfiguration")}
                label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.EditInRRows)}
              />
            </Grid>
          </Grid>
        ) : currentTab === InvoiceEnums.InvoiceProductTabs.Periodisation ? (
          <Grid container direction="column">
            <Grid item className={cssExports.tableMasterRowController}>
              <HorizontalFormField<ToggleProps>
                Component={Toggle}
                {...connect("isPeriodisationAdvancedConfiguration")}
                label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.EditInRRows)}
              />
            </Grid>
            {values.isPeriodisationAdvancedConfiguration && !isRecurring ? (
              <Grid item className={cssExports.tableMasterRowController}>
                <HorizontalFormField<ToggleProps>
                  Component={Toggle}
                  {...connect("isPeriodisationByDate")}
                  label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.PeriodiseOnDate)}
                />
              </Grid>
            ) : (
              <></>
            )}
          </Grid>
        ) : (
          <></>
        )}
        <Grid container justifyContent="space-between" alignItems="flex-start">
          <Grid xs item>
            {currentTab === InvoiceEnums.InvoiceProductTabs.Dimensions ? (
              <Grid item className={cssExports.editableTableFooterDimensions}>
                <DimensionChipSelector
                  key={values.isDimensionsAdvancedConfiguration ? "advanced" : "default"}
                  readonly
                  {...connect("products")}
                  onChange={() => {}}
                  valueFormatter={
                    values.isDimensionsAdvancedConfiguration
                      ? invoiceService.getRowDimensionData
                      : invoiceService.getRowDimensionDataAdvancedConfig.bind(null, totalAmount)
                  }
                  type={DimensionEnums.DimensionCategory.Invoice}
                  currency={values.currency}
                  amount={totalAmount}
                  alwaysDisplayAmounts
                />
              </Grid>
            ) : currentTab === InvoiceEnums.InvoiceProductTabs.Periodisation ? (
              <>
                {isRecurring && (
                  <SimpleMessage>
                    {LocalizationHelperInstance.getFormattedMessage(
                      TranslationKeys.Invoice.RecurringPeriodisationHelpText
                    )}
                  </SimpleMessage>
                )}
              </>
            ) : (
              <>
                <Suggestions
                  setValue={onArticleSelect.bind(undefined, values.products?.length ?? 0)}
                  suggestions={topArticles as SelectionItem[]}
                  onAddButtonClick={onAddArticle}
                  autogeneratedText={LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.Invoice.BasedOnPrevious
                  )}
                />
                {props.additionalRowActions}
              </>
            )}
          </Grid>
          <Grid item>
            <Grid item container justifyContent="flex-end" columnGap={2} flexWrap="nowrap">
              <Grid item>
                {!vatLockedToVatFree && (
                  <CurrencyDisplay
                    value={totalVAT}
                    rightAlign
                    label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.VAT)}
                  />
                )}
              </Grid>
              <Grid item>
                <CurrencyDisplay
                  value={totalAmount}
                  rightAlign
                  label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.TotalPriceExVAT)}
                />
              </Grid>
              <Grid item className={cssExports.footerCurrency}>
                <CurrencySelectionInput {...connect("currency")} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </WizardSection>
  );
};

export default ContentSelectionStep;
